import { Fragment, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { Color, FILE_KIND_ICON, Size, fileKindOf, t, type AvatarPerson, type IconMeaning } from '@bms2/shared';
import { Button, Pill } from '../controls';
import { Icon } from '../Icon';
import { AttendeeAvatar } from './AttendeeAvatar';
import styles from './FileRow.module.css';

/**
 * 🔒 One File row, drawn by a record's Files tile and by a document's Files
 * block alike (decision 3010, amending 0457). 💰 The two drew one file two
 * ways: the tile's row opened the file, named its scan, its restriction and its
 * status and carried Share and the pencil, and the document's row named the
 * file and nothing a person could do with it (product owner, 2026-10-11).
 *
 * The row holds no rule of its own: what it says is what its caller hands it.
 */

/** A small status-coloured pill on a file row: its restriction, its status. */
export interface FileRowPill {
  text: string;
  group: 'good' | 'warning' | 'error' | 'inactive';
}

export interface FileRowProps {
  name: string;
  /** 🔒 The file's description in the uploader's words: the third line where neither the Verified line nor Scan pending stands, else the name's title (decision 3013). */
  description?: string | null;
  /** How many versions the file is on; past the first it reads vN on the first line. */
  version?: number | null;
  /** A third line for a row that carries no description, such as an asset file's date. */
  detail?: string | null;
  /** 🔒 Decision 11301: the date the person gave the document at its upload, worded by the caller, on the first line after the version. */
  dated?: string | null;
  /** 🔒 Decision 1182: a quarantined file's sentence, why and whom to ask, on a line of its own. */
  quarantine?: string | null;
  /** 🔒 Decision 7420: the file's bytes are stored encrypted, drawn as a lock beside the name. */
  encrypted?: boolean;
  /** An address inside the application: a Knowledge Base article on an asset's files. */
  route?: string | null;
  /** The File Access Proxy's address, which opens the file in its own tab. */
  href?: string | null;
  /** 🔒 The served version's media type, which with the name's extension picks the row's icon (decision 3011). */
  mimeType?: string | null;
  /** The file's type, drawn as the tinted chip (decision 0376). */
  chip?: string | null;
  /** What the file is narrowed to: Confidential, a class, Unclassified (piece 9). */
  flag?: FileRowPill | null;
  /** The file's own status pill: since decision 3012, Quarantined alone, because the Verified line says the rest. */
  pill?: FileRowPill | null;
  /** 🔒 Decision 3012: the scan's mark, at the foot's left (decision 7080) or as the last line. */
  scan?: FileRowScan | null;
  /** 🔒 Decision 3012: the Verified line, on a verified file alone. */
  verified?: FileRowVerified | null;
  /**
   * Share (decision 0345), or on a Deal's file Release, with Share as the second
   * button (decision 6040). The icon is the narrow row's glyph for it, Share's by default.
   */
  button?: FileRowButton | null;
  /** 🔒 A second labelled action after the first, as the full-width row draws its two: the Deal's Share beside Release (decision 6040). */
  secondButton?: FileRowButton | null;
  /**
   * 🔒 The record the file reached this tile through, a read-only label of its own
   * beside the type chip (decision 6040): a Deal's name on the Company page. A row
   * the file reached directly carries none, and nothing is drawn in its place.
   */
  origin?: string | null;
  /** File details (decision 0273). */
  onEdit?: (() => void) | null;
  /** Attributes the row's own element carries, a suite's handle on one file. */
  data?: Readonly<Record<string, string>>;
}

/** One of the row's labelled actions, and the glyph the narrow row draws for it. */
export interface FileRowButton {
  label: string;
  onAct: () => void;
  icon?: IconMeaning;
}

/**
 * 🔒 Decision 3012: a file's scan as the row draws it. A Clean version carries
 * the green shield and the scanner's name at the card's foot, its date and time
 * in the title; a Pending one reads Scan pending as the row's last line.
 */
export type FileRowScan = { state: 'clean'; scanner: string | null; title: string } | { state: 'pending' };

/** 🔒 Decision 3012: a verified file's last line, the verifier's avatar where one is recorded, and who and when in its title. */
export interface FileRowVerified {
  person: AvatarPerson | null;
  title: string;
}

const DETAIL_SEPARATOR = ' · ';

/** The detail line: the parts the caller joined, the line breaking between them. */
function Detail({ text }: { text: string }) {
  return (
    <span className={styles.detail}>
      {text.split(DETAIL_SEPARATOR).map((part, index) => (
        <Fragment key={`${part}-${index}`}>
          {index > 0 ? DETAIL_SEPARATOR : null}
          <span className={styles.detailPart}>{part}</span>
        </Fragment>
      ))}
    </span>
  );
}

function rowPill(pill: FileRowPill | null | undefined): ReactNode {
  if (!pill) return null;
  return (
    <span className={styles.pill} data-tile-pill={pill.group}>
      <Pill text={pill.text} foreground={`var(--status-${pill.group}-text)`} background={`var(--status-${pill.group}-bg)`} size={Size.Small} />
    </span>
  );
}

/** 🔨 The file's own type, the tinted non-status chip with an edge in its own ink (decision 0376). */
function chipOf(chip: string | null | undefined): ReactNode {
  if (!chip) return null;
  return (
    <span className={styles.chip} data-tile-chip={chip}>
      <Pill text={chip} foreground="var(--tag-slate-text)" background="var(--tag-slate-bg)" size={Size.Small} outlined asTyped />
    </span>
  );
}

/** 🔒 The record a file came through, a tinted label of its own beside the type chip, drawn as typed (decision 6040). */
function originOf(origin: string | null | undefined): ReactNode {
  if (!origin) return null;
  return (
    <span className={styles.origin} data-file-origin={origin}>
      <Pill text={origin} foreground="var(--tag-blue-text)" background="var(--tag-blue-bg)" size={Size.Small} asTyped />
    </span>
  );
}

/** The narrow card's small icon button for one action, its label its title and its name for a screen reader. */
function iconButtonOf(button: FileRowButton | null, which: 'first' | 'second'): ReactNode {
  if (!button) return null;
  const icon = button.icon ?? 'share';
  return (
    <button type="button" className={styles.iconButton} onClick={button.onAct} aria-label={button.label} title={button.label} data-file-icon-button={which}>
      <Icon meaning={icon} size="inline" />
    </button>
  );
}

/** A row that names a route navigates inside the application; outside a router the anchor stands in. */
function RouteLink({ route, title, children }: { route: string; title: string | undefined; children: ReactNode }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link to={route} className={styles.name} title={title}>
      {children}
    </Link>
  ) : (
    <a href={route} className={styles.name} title={title}>
      {children}
    </a>
  );
}

export function FileRow({ name, description = null, version = null, detail = null, dated = null, quarantine = null, encrypted = false, route = null, href = null, mimeType = null, chip = null, flag = null, pill = null, scan = null, verified = null, button = null, secondButton = null, origin = null, onEdit = null, data }: FileRowProps) {
  const kind = fileKindOf(mimeType, name);
  // 🔒 Decision 3013: the third line is the Verified line, else Scan pending, else the description; a description it cannot hold is the name's title.
  const third: ReactNode = verified ? (
    <span className={styles.verified} title={verified.title} data-file-verified="true">
      {/* The avatar is the picture of who; the title says it in words, kept for a screen reader beside the word. */}
      {verified.person ? <span aria-hidden="true" className={styles.verifier}><AttendeeAvatar person={verified.person} attending="NeedsAction" /></span> : null}
      <span className={styles.verifiedWord}>{t('file.verified')}</span>
      {verified.title !== t('file.verified') ? <span className={styles.srOnly}>{verified.title}</span> : null}
    </span>
  ) : scan?.state === 'pending' ? (
    <span className={styles.pending} data-file-scan="pending">{t('file.scan.pending')}</span>
  ) : description || detail ? (
    <Detail text={description ?? detail ?? ''} />
  ) : null;
  const nameTitle = description && (verified || scan?.state === 'pending') ? description : undefined;
  // 🔒 Decision 7420: a lock beside the name of a file whose bytes are stored encrypted, its word its title and its name for a screen reader.
  const nameText = (
    <>
      {encrypted ? <span className={styles.lock} title={t('file.encrypted')} aria-label={t('file.encrypted')} role="img" data-file-encrypted="true"><Icon meaning="fileEncrypted" size="inline" /></span> : null}
      <span className={styles.nameText}>{name}</span>
    </>
  );
  const nameLink = href ? (
    // The bytes come from the File Access Proxy, an address outside the router, so the name is a plain link that opens in its own tab.
    <a href={href} className={styles.name} target="_blank" rel="noopener noreferrer" title={nameTitle} data-file-href="true">{nameText}</a>
  ) : route ? (
    <RouteLink route={route} title={nameTitle}>{nameText}</RouteLink>
  ) : (
    <span className={styles.name} title={nameTitle}>{nameText}</span>
  );
  const versionText = version !== null && version > 1 ? `v${version}` : null;
  const actions =
    button || secondButton || onEdit ? (
      <>
        {/* 🔒 Decision 7080: narrow, Share and the pencil are small icon buttons at the right of the card's foot; a second action stands after the first (decision 6040). */}
        <span className={styles.narrowActions} data-file-actions="narrow">
          {iconButtonOf(button, 'first')}
          {iconButtonOf(secondButton, 'second')}
          {onEdit ? (
            <button type="button" className={styles.iconButton} onClick={onEdit} aria-label={t('file.details.edit')} title={t('file.details.edit')} data-file-edit-icon="true">
              <Icon meaning="edit" size="inline" />
            </button>
          ) : null}
        </span>
        {/* 🔒 Wide, the same acts stand as labelled buttons in the foot (decision 3013, placed by decision 7080). */}
        <span className={styles.wideActions} data-file-actions="wide">
          {button ? (
            // Share on the file row (decision 0345), or Release on a Deal's: a labelled button.
            <span className={styles.button} data-file-button="true">
              <Button size={Size.Small} color={Color.Inactive} text={[button.label]} actions={[button.onAct]} />
            </span>
          ) : null}
          {secondButton ? (
            <span className={styles.button} data-file-second-button="true">
              <Button size={Size.Small} color={Color.Inactive} text={[secondButton.label]} actions={[secondButton.onAct]} />
            </span>
          ) : null}
          {onEdit ? (
            <button type="button" className={styles.edit} onClick={onEdit} aria-label={t('file.details.edit')} data-file-edit="true">
              <Icon meaning="edit" size="inline" />
            </button>
          ) : null}
        </span>
      </>
    ) : null;
  // 🔒 Decision 7080: a clean version's shield and scanner stand at the foot's left, the acts at its right.
  const cleanScan =
    scan?.state === 'clean' ? (
      <span className={styles.scanMark} title={scan.title} data-file-scan="clean">
        <Icon meaning="scanClean" />
        {scan.scanner ? <span className={styles.scanner}>{scan.scanner}</span> : null}
      </span>
    ) : null;
  return (
    <li className={styles.row} data-file-row="true" {...data}>
      <div className={styles.frame} data-file-card="true">
        <div className={styles.box}>
          {/* 🔒 The file type's icon, two text lines tall, its hover title the file's name (decisions 3011, 7080). */}
          <span className={styles.kind} title={name} data-file-kind={kind}>
            <Icon meaning={FILE_KIND_ICON[kind]} />
          </span>
          <span className={styles.text}>
            {/* 🔒 Decision 3013: the type chip and the category pill side by side, then the version past the first; the origin follows the type (decision 6040). */}
            {chip || origin || flag || versionText || dated ? (
              <span className={styles.kindLine} data-file-line="kind">
                {chipOf(chip)}
                {originOf(origin)}
                {flag ? <span data-tile-flags="true">{rowPill(flag)}</span> : null}
                {versionText ? <span className={styles.version}>{versionText}</span> : null}
                {dated ? <span className={styles.version} data-file-dated="true">{dated}</span> : null}
              </span>
            ) : null}
            <span className={styles.nameLine} data-file-line="name">
              {nameLink}
            </span>
            {third ? <span className={styles.stateLine} data-file-line="state">{third}</span> : null}
            {quarantine ? <span className={styles.quarantine} data-file-quarantine="true">{quarantine}</span> : null}
          </span>
          {/* 🔒 Decision 1182: a quarantined file's pill keeps its place at the box's right, a warning. */}
          {pill ? <span className={styles.flags} data-file-status="true">{rowPill(pill)}</span> : null}
        </div>
        {cleanScan || actions ? (
          <div className={styles.foot} data-file-foot="true">
            {cleanScan}
            {actions}
          </div>
        ) : null}
      </div>
    </li>
  );
}
